import { DEFAULT_TIMEZONE, formatAED, localDate, zonedDateTime } from "@teamna/shared";

/**
 * Rendering helpers for time.
 *
 * Every clock time in this app is the business's wall clock, never the browser's. A member opening
 * the schedule from a holiday in London has to read the same "5:00 PM" the business printed on the
 * timetable, so the timezone travels with the value rather than being assumed. V1 is UAE only, so
 * the fallback is `Asia/Dubai` from `@teamna/shared`.
 *
 * Day grouping goes through `localDate` from the shared package, which is the same function the API
 * uses to decide which calendar day a session belongs to. The app and the server therefore cannot
 * disagree about where a late evening session lands.
 */

/**
 * Newer ICU data (Chrome, Linux Bun) writes the short September as "Sept", older data (macOS Bun,
 * Safari) as "Sep". One spelling everywhere, so a screen reads the same in every browser and a test
 * passes on every machine.
 */
export function normaliseMonths(text: string): string {
  return text.replace(/\bSept\b/g, "Sep");
}

function safeFormat(iso: string, options: Intl.DateTimeFormatOptions, timezone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  try {
    return normaliseMonths(
      new Intl.DateTimeFormat("en-GB", {
        ...options,
        timeZone: timezone ?? DEFAULT_TIMEZONE,
      }).format(date),
    );
  } catch {
    return normaliseMonths(new Intl.DateTimeFormat("en-GB", options).format(date));
  }
}

/**
 * "5:00 PM"
 *
 * en-GB renders the meridiem lowercase, and the time rail on a session card sets it uppercase, so
 * it is normalised here. Two spellings of the same half of the day on one screen reads as a bug.
 */
export function formatTime(iso: string, timezone?: string): string {
  return safeFormat(iso, { hour: "numeric", minute: "2-digit", hour12: true }, timezone).replace(
    /(am|pm)$/i,
    (match) => match.toUpperCase(),
  );
}

/** The clock face on its own, for the time rail: "5:00". */
export function formatClock(iso: string, timezone?: string): string {
  return safeFormat(iso, { hour: "numeric", minute: "2-digit", hour12: true }, timezone)
    .replace(/\s?(am|pm)$/i, "")
    .trim();
}

/** "PM", the other half of the time rail. */
export function formatMeridiem(iso: string, timezone?: string): string {
  const match = /(am|pm)$/i.exec(formatTime(iso, timezone).trim());
  return match ? match[1]!.toUpperCase() : "";
}

/** "Tue 2 Sep" */
export function formatDayShort(iso: string, timezone?: string): string {
  return safeFormat(iso, { weekday: "short", day: "numeric", month: "short" }, timezone);
}

/** "Tuesday 2 September" */
export function formatDayLong(iso: string, timezone?: string): string {
  return safeFormat(iso, { weekday: "long", day: "numeric", month: "long" }, timezone);
}

/** "Tue 2 Sep, 5:00 PM", the one-line answer to "when is it". */
export function formatDateTime(iso: string, timezone?: string): string {
  return `${formatDayShort(iso, timezone)}, ${formatTime(iso, timezone)}`;
}

/** "2 Sep 2026", for expiry dates and due dates. */
export function formatDate(iso: string, timezone?: string): string {
  return safeFormat(iso, { day: "numeric", month: "short", year: "numeric" }, timezone);
}

/**
 * Money for display.
 *
 * `formatAED` from `@teamna/shared` does the arithmetic, because fils are integers and dividing by
 * a hundred in a component is how rounding bugs get into prices. The currency travels with the
 * amount on every billing row, so it is passed through as the symbol rather than assumed: V1 is
 * AED only, and the day a business is billed in something else this keeps telling the truth.
 */
export function formatMoney(fils: number, currency = "AED"): string {
  return formatAED(fils, { symbol: currency });
}

/** Calendar key used to group an agenda into day sections, as `yyyy-MM-dd`. */
export function dayKey(iso: string, timezone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return localDate(date, timezone ?? DEFAULT_TIMEZONE);
}

/** "Today", "Tomorrow", "Yesterday", or the short day label. */
export function formatRelativeDay(iso: string, timezone?: string): string {
  const today = dayKey(new Date().toISOString(), timezone);
  const tomorrow = dayKey(new Date(Date.now() + 86_400_000).toISOString(), timezone);
  const yesterday = dayKey(new Date(Date.now() - 86_400_000).toISOString(), timezone);
  const target = dayKey(iso, timezone);
  if (target === today) return "Today";
  if (target === tomorrow) return "Tomorrow";
  if (target === yesterday) return "Yesterday";
  return formatDayShort(iso, timezone);
}

/**
 * The heading of one day: "Today, Thursday 10 September" for yesterday, today and tomorrow, the
 * full date for any other day. The phone day list and the Calendar's Day view both use it.
 */
export function formatDayHeading(iso: string, timezone?: string): string {
  const relative = formatRelativeDay(iso, timezone);
  if (relative === "Today" || relative === "Tomorrow" || relative === "Yesterday") {
    return `${relative}, ${formatDayLong(iso, timezone)}`;
  }
  return formatDayLong(iso, timezone);
}

/**
 * "in 3 hours", "in 2 days", "12 minutes ago". Plain words, no jargon.
 *
 * Shown next to the start time on a booking, because "in 3 hours" is what decides whether the
 * member is still inside the free cancellation window, and counting that out of a date is work.
 *
 * Past a day the count is calendar days in the business's timezone, not elapsed hours divided by 24.
 * The agenda groups the same booking under "Tomorrow", and a detail screen that answered "in 2
 * days" because the gap happened to round up read as two different facts about one session.
 */
export function formatCountdown(iso: string, timezone?: string): string {
  const diffMs = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(diffMs)) return "";
  const past = diffMs < 0;
  const minutes = Math.round(Math.abs(diffMs) / 60_000);

  let value: number;
  let unit: string;
  if (minutes < 60) {
    value = Math.max(1, minutes);
    unit = "minute";
  } else if (minutes < 60 * 24) {
    value = Math.round(minutes / 60);
    unit = "hour";
  } else {
    value = Math.max(1, calendarDaysBetween(new Date().toISOString(), iso, timezone));
    unit = "day";
  }

  const label = `${value} ${unit}${value === 1 ? "" : "s"}`;
  return past ? `${label} ago` : `in ${label}`;
}

/** Whole calendar days between two instants, read in the business's timezone. Always positive. */
function calendarDaysBetween(fromIso: string, toIso: string, timezone?: string): number {
  const from = Date.parse(`${dayKey(fromIso, timezone)}T00:00:00Z`);
  const to = Date.parse(`${dayKey(toIso, timezone)}T00:00:00Z`);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return 0;
  return Math.abs(Math.round((to - from) / 86_400_000));
}

/** True once an instant is in the past. Its own function so a component never reads the clock. */
export function hasStarted(iso: string): boolean {
  const time = new Date(iso).getTime();
  return Number.isFinite(time) && time < Date.now();
}

/** Days remaining before an expiry date, floored at 0. */
export function daysUntil(iso: string): number {
  const remaining = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(remaining)) return 0;
  return Math.max(0, Math.ceil(remaining / 86_400_000));
}

/**
 * Turns whatever is in a date field into `yyyy-MM-dd` as it is typed.
 *
 * A date of birth is entered on a phone keyboard without a picker, so the separators are derived
 * from the digits and never held on to. Deleting then works the way it looks like it should
 * instead of stranding a hyphen. Mirrors `maskIsoDate` in the mobile app.
 */
export function maskIsoDate(text: string): string {
  const digits = text.replace(/\D/g, "").slice(0, 8);
  return [digits.slice(0, 4), digits.slice(4, 6), digits.slice(6, 8)]
    .filter((part) => part.length > 0)
    .join("-");
}

/** True when the text is a real calendar date, not merely the right shape. */
export function isValidIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number) as [number, number, number];
  if (month < 1 || month > 12 || day < 1) return false;
  const lastDayOfMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return day <= lastDayOfMonth;
}

/** "Tue", for a strip of day chips. */
export function formatWeekdayShort(iso: string, timezone?: string): string {
  return safeFormat(iso, { weekday: "short" }, timezone);
}

/** "10", the date of the month on its own, for a day in the week strip. */
export function formatDayOfMonth(iso: string, timezone?: string): string {
  return safeFormat(iso, { day: "numeric" }, timezone);
}

/**
 * "8 - 14 Sep", or "29 Sep - 5 Oct" when the week straddles two months.
 *
 * The header of a week view has to answer "which week" without the reader doing arithmetic, and
 * "This week" alone does not: it is equally true of every day in it, which is exactly the
 * complaint that a seven-day list reads as today's list.
 */
export function formatWeekRange(fromIso: string, toIso: string, timezone?: string): string {
  const sameMonth =
    safeFormat(fromIso, { month: "short" }, timezone) ===
    safeFormat(toIso, { month: "short" }, timezone);
  const from = sameMonth
    ? safeFormat(fromIso, { day: "numeric" }, timezone)
    : safeFormat(fromIso, { day: "numeric", month: "short" }, timezone);
  const to = safeFormat(toIso, { day: "numeric", month: "short" }, timezone);
  return `${from} - ${to}`;
}

const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

/** A schedule template's `weekday` (0 for Sunday, matching Postgres `dow`) as a word. */
export function weekdayName(weekday: number): string {
  return WEEKDAY_NAMES[weekday] ?? "";
}

/** "17:00" to "5:00 PM", for a template's local start time. */
export function formatLocalTime(hhmm: string): string {
  const [hourText, minuteText = "00"] = hhmm.split(":");
  const hour = Number(hourText);
  if (!Number.isFinite(hour)) return hhmm;
  const suffix = hour >= 12 ? "PM" : "AM";
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:${minuteText} ${suffix}`;
}

/**
 * The reverse of every other function here: a wall clock the owner picked, turned into the UTC
 * instant the API stores.
 *
 * Mirrors `instantFromZonedTime` in `apps/mobile/src/lib/format.ts`, but delegates the actual
 * conversion to `zonedDateTime` from `@teamna/shared`, which is the same function the session
 * generator uses to turn "Tuesdays at 17:00" into a row. An owner picking 5pm means 5pm at the
 * business, whatever the laptop or the phone they picked it on thinks the time is.
 */
export function instantFromZonedTime(
  dateKey: string,
  hour: number,
  minute: number,
  timezone?: string,
): string {
  const time = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  try {
    return zonedDateTime(dateKey, time, timezone ?? DEFAULT_TIMEZONE).toISOString();
  } catch {
    // A malformed date key or an unknown zone. Falling back to now keeps the picker usable and the
    // API rejects anything nonsensical anyway.
    return new Date().toISOString();
  }
}

/** "45 min", from the two ends of a session. */
export function formatDuration(startsAt: string, endsAt: string): string {
  const minutes = Math.round((new Date(endsAt).getTime() - new Date(startsAt).getTime()) / 60_000);
  if (!Number.isFinite(minutes) || minutes <= 0) return "";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
}

/** ISO instant for the start of today, used as the default schedule window start. */
export function startOfTodayIso(): string {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date.toISOString();
}

/**
 * Start of the day `days` ago, the other end of the schedule window.
 *
 * Rounded to a day boundary for the same reason as `inDaysIso`: this value is part of a query key,
 * and a key carrying the current millisecond is a different key on every render.
 */
export function daysAgoIso(days: number): string {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - days);
  return date.toISOString();
}

/**
 * End of the day `days` from now, used as the default schedule window end.
 *
 * Rounded to a day boundary on purpose. These values are query keys, and a key that carries the
 * current millisecond is a different key on every render: TanStack Query then refetches, the
 * response re-renders, the next render builds another key, and the screen sits on its skeletons
 * hammering the API for as long as it is open.
 */
export function inDaysIso(days: number): string {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + days);
  date.setHours(23, 59, 59, 999);
  return date.toISOString();
}
